import { format, parseISO } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useShallow } from 'zustand/shallow';

import { showAlert } from '@/components/ui/alert';
import { STRINGS } from '@/constants/strings';
import { useGuardianStackNavigation } from '@/hooks';
import { shouldSuppressErrorAlert } from '@/libs/errors/request-timeout';
import { signInWithEmailAndPassword, signOut } from '@/libs/firebase/auth';
import { scheduleAccountDeletion } from '@/services/schedule-account-deletion';
import { useSessionStore } from '@/stores/session-store';
import { useUiStore } from '@/stores/ui-store';

const strings = STRINGS.guardianProfile.deleteAccount;
const { reasonMaxLength } = strings;

/** Format the ISO deletion deadline (local time) as e.g. "15:30 on July 16, 2026". */
function formatDeletionDeadline(iso: string): string {
  const date = parseISO(iso);
  return Number.isNaN(date.getTime()) ? '' : format(date, "HH:mm 'on' MMMM d, yyyy");
}

/** Sign out of Firebase and clear the local session so the root navigator lands on Welcome. */
function hardLogout(): void {
  signOut().catch(() => undefined);
  useSessionStore.getState().resetSessionState();
}

/**
 * Delete Account flow. Reauthenticates the guardian with their password (Firebase), then
 * asks the server to schedule permanent deletion in 30 days (BR-03) — which also emails
 * admin the reason. On success it shows a Notice with the deletion deadline; tapping OK
 * hard-logs-out (clears the session) so the root navigator routes back to Welcome. A wrong
 * password surfaces inline under the password field.
 */
export function useGuardianDeleteAccount() {
  const navigation = useGuardianStackNavigation();
  const user = useSessionStore(state => state.user);
  const { showGlobalLoading, hideGlobalLoading } = useUiStore(
    useShallow(s => ({
      showGlobalLoading: s.showGlobalLoading,
      hideGlobalLoading: s.hideGlobalLoading,
    })),
  );

  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [password, setPassword] = useState('');
  const [secureTextEntry, setSecureTextEntry] = useState(true);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Permanently Delete enables only once the box is ticked AND a password is entered (TC-02–04).
  const canSubmit = useMemo(
    () => acknowledged && password.trim().length > 0,
    [acknowledged, password],
  );

  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const onChangeReason = useCallback((text: string) => {
    // Guard the 200-char cap even if the field's maxLength is bypassed (TC-09).
    setReason(text.slice(0, reasonMaxLength));
  }, []);

  const onToggleAcknowledged = useCallback((value: boolean) => {
    setAcknowledged(value);
  }, []);

  const onChangePassword = useCallback((text: string) => {
    setPassword(text);
    setPasswordError(prev => (prev ? null : prev));
  }, []);

  const handleDelete = useCallback(async () => {
    if (!canSubmit) return;

    const email = user?.email?.trim();
    if (!email) {
      showAlert({
        title: STRINGS.common.noticeTitle,
        message: strings.errorSessionExpired,
        buttons: [{ text: STRINGS.common.buttons.dismiss }],
      });
      return;
    }

    try {
      showGlobalLoading();

      // 1. Reauthenticate to validate the password (mirrors the Change Password flow).
      await signInWithEmailAndPassword(email, password);

      // 2. Ask the server to schedule deletion in 30 days + notify admin with the reason.
      const { scheduledAt } = await scheduleAccountDeletion({
        reason: reason.trim() || undefined,
        acknowledged,
      });

      // 3. Confirm the deletion deadline; the full sign-out only happens once the user taps OK.
      showAlert({
        title: STRINGS.common.noticeTitle,
        message: strings.successMessage(formatDeletionDeadline(scheduledAt)),
        buttons: [{ text: strings.successConfirm, onPress: hardLogout }],
      });
    } catch (e) {
      if (shouldSuppressErrorAlert(e)) {
        return;
      }

      const code = (e as { code?: string })?.code ?? '';
      const isWrongPassword = code === 'auth/wrong-password' || code === 'auth/invalid-credential';

      if (isWrongPassword) {
        setPasswordError(strings.errorIncorrectPassword);
        return;
      }

      showAlert({
        title: STRINGS.common.noticeTitle,
        message: strings.errorGeneric,
        buttons: [{ text: STRINGS.common.buttons.dismiss }],
      });
    } finally {
      hideGlobalLoading();
    }
  }, [
    acknowledged,
    canSubmit,
    hideGlobalLoading,
    password,
    reason,
    showGlobalLoading,
    user?.email,
  ]);

  return {
    reason,
    acknowledged,
    password,
    secureTextEntry,
    passwordError,
    canSubmit,
    reasonMaxLength,
    setSecureTextEntry,
    onChangeReason,
    onToggleAcknowledged,
    onChangePassword,
    handleBack,
    handleDelete,
  };
}

export default useGuardianDeleteAccount;
